/* eslint-disable max-lines */
import {
  PaymentIntent,
  PaymentMethodResult,
  PaymentRequestPaymentMethodEvent,
  Stripe,
  StripeCardNumberElement,
  StripeError,
} from '@stripe/stripe-js'
import { paymentApi, userApi } from 'api'
import i18n from 'i18next'

import {
  setErrorAction,
  startFetching,
  stopFetching,
} from 'root-redux/actions/common'
import {
  getUserStatusAction,
  sendUserConfigAction,
  updateUserConfigAction,
} from 'root-redux/actions/user'
import {
  selectCurrentVariantCohort,
  selectScreenName,
  selectStripeAccountId,
  selectStripeAccountName,
} from 'root-redux/selects/common'
import {
  selectEmail,
  selectUUID,
  selectUpiPurchaseData,
  selectUserPaymentMethod,
  selectUserPaymentSystem,
} from 'root-redux/selects/user'

import { captureFunnelError } from 'helpers/captureFunnelError'
import { createIntroOfferProductId } from 'helpers/createIntroOfferProductId'
import { createProductId } from 'helpers/createProductId'
import { FunnelErrorCode } from 'helpers/funnelErrors'
import { getAxonCheckoutParams } from 'helpers/getAxonCheckoutParams'
import { logAddToCartPixelEvents } from 'helpers/logAddToCartPixelEvents'

import {
  DEFAULT_CARDHOLDER_NAME,
  PaymentMethod,
  PaymentSystem,
  SourceStep,
  StripeDeclineReason,
  StripeSoftDeclineReason,
  UPI_BILLING_COUNTRY,
} from 'modules/purchase/constants'
import { getPurchaseFailedEventParams } from 'modules/purchase/helpers/getPurchaseFailedEventParams'
import { getPurchaseStartedEventParams } from 'modules/purchase/helpers/getPurchaseStartedEventParams'
import { getPurchaseSuccessEventParams } from 'modules/purchase/helpers/getPurchaseSuccessEventParams'
import { logSuccessfulUpiPayment } from 'modules/purchase/helpers/logSuccessfulUpiPayment'
import {
  checkIsRetryAllowed,
  getRedirectUrl,
  logFailedPayment,
  logSuccessfulPayment,
} from 'modules/purchase/helpers/rootHelpers'

import { IAction, IAppState, TAppDispatchThunk } from 'models/store.model'
import { ISubscription } from 'models/subscriptions.model'
import { IUpiPurchaseData } from 'models/user.model'

import { Events, eventLogger } from 'services/eventLogger.service'

import { CENTS_IN_DOLLAR, Cohort } from 'root-constants'

import {
  selectCreatedSubscriptionId,
  selectCurrency,
  selectPaymentClientSecret,
  selectPlanId,
  selectProductId,
  selectSubscription,
  selectSubscriptionFullPrice,
  selectSubscriptionLookupKey,
  selectSubscriptionPeriodName,
  selectSubscriptionPeriodQuantity,
  selectSubscriptionTrialLookupKey,
  selectSubscriptionTrialPeriodDays,
  selectSubscriptionTrialPeriodPrice,
  selectTrialPeriodDays,
} from '../selects/common'

const MODULE_NAME = 'PURCHASE'

export const SET_SUBSCRIPTION = `${MODULE_NAME}/SET_SUBSCRIPTION`
export const SET_TAX_AMOUNT = `${MODULE_NAME}/SET_TAX_AMOUNT`
export const PURCHASE = `${MODULE_NAME}/PURCHASE`
export const CHECK_3D_SECURE = `${MODULE_NAME}/CHECK_3D_SECURE`
export const SET_3D_SECURE_IFRAME_URL = `${MODULE_NAME}/SET_3D_SECURE_IFRAME_URL`
export const RESET_3D_SECURE_IFRAME_URL = `${MODULE_NAME}/RESET_3D_SECURE_IFRAME_URL`
export const SET_PAYMENT_CLIENT_SECRET = `${MODULE_NAME}/SET_PAYMENT_CLIENT_SECRET`
export const SET_TRIAL_PERIOD_DAYS = `${MODULE_NAME}/SET_TRIAL_PERIOD_DAYS`
export const SET_CREATED_SUBSCRIPTION_ID = `${MODULE_NAME}/SET_CREATED_SUBSCRIPTION_ID`
export const CHECK_PAYMENT_REQUEST_BUTTON = `${MODULE_NAME}/CHECK_PAYMENT_REQUEST_BUTTON`
export const CHECK_PAYPAL_REQUEST_BUTTON = `${MODULE_NAME}/CHECK_PAYPAL_REQUEST_BUTTON`
export const SET_PAYMENT_METHOD = `${MODULE_NAME}/SET_PAYMENT_METHOD`
export const SET_AVAILABLE_ALTERNATIVE_PAYMENT_METHODS = `${MODULE_NAME}/SET_AVAILABLE_ALTERNATIVE_PAYMENT_METHODS`
export const MAKE_INAPP_UPSELL = `${MODULE_NAME}/MAKE_INAPP_UPSELL`
export const MAKE_UPSELL = `${MODULE_NAME}/MAKE_UPSELL`
export const SET_IS_PRINTABLE_PDF_ENABLED = `${MODULE_NAME}/SET_IS_PRINTABLE_PDF_ENABLED`
export const SET_WAS_UPI_PAYMENT_INITIATED = `${MODULE_NAME}/SET_WAS_UPI_PAYMENT_INITIATED`

const set3DSecureIframeUrlAction = (payload: string): IAction<string> => ({
  type: SET_3D_SECURE_IFRAME_URL,
  payload,
})

const reset3DSecureIframeUrlAction = (): IAction<any> => ({
  type: RESET_3D_SECURE_IFRAME_URL,
})

const setPaymentClientSecretAction = (payload: string): IAction<string> => ({
  type: SET_PAYMENT_CLIENT_SECRET,
  payload,
})

const setTrialPeriodDaysAction = (payload: number): IAction<number> => ({
  type: SET_TRIAL_PERIOD_DAYS,
  payload,
})

const setCreatedSubscriptionIdAction = (payload: string): IAction<string> => ({
  type: SET_CREATED_SUBSCRIPTION_ID,
  payload,
})

const getErrorActionPayload = ({ type, message }: StripeError): string =>
  message || type

export const setSelectedSubscriptionAction = (
  payload: ISubscription,
): IAction<ISubscription> => ({
  type: SET_SUBSCRIPTION,
  payload,
})

export const setTaxAmountAction = (payload: number): IAction<number> => ({
  type: SET_TAX_AMOUNT,
  payload,
})

export const setIsPrintablePdfEnabled = (
  payload: boolean,
): IAction<boolean> => ({
  type: SET_IS_PRINTABLE_PDF_ENABLED,
  payload,
})

export const setPaymentMethodAction = (
  payload: PaymentMethod,
): IAction<PaymentMethod> => ({
  type: SET_PAYMENT_METHOD,
  payload,
})

export const setWasUpiPaymentInitiatedAction = (
  payload: boolean,
): IAction<boolean> => ({
  type: SET_WAS_UPI_PAYMENT_INITIATED,
  payload,
})

export const setAvailableAlternativePaymentMethods = (
  payload: PaymentMethod,
): IAction<PaymentMethod[]> => ({
  type: SET_AVAILABLE_ALTERNATIVE_PAYMENT_METHODS,
  payload: [payload],
})

export const sendEventsForAppleGooglePay =
  ({
    paymentMethod,
    sourceStep,
  }: {
    paymentMethod: PaymentMethod
    sourceStep: SourceStep
  }): any =>
  async (
    dispatch: TAppDispatchThunk<any>,
    getState: () => IAppState,
  ): Promise<void> => {
    const state = getState()
    const selectedSubscription = selectSubscription(state)
    const commonPurchaseStartedParams = getPurchaseStartedEventParams(state)
    const isCheckoutSkipped = sourceStep === SourceStep.PAYWALL

    eventLogger.logPurchaseStarted({
      paymentMethod,
      sourceStep,
      paymentSystem: PaymentSystem.STRIPE,
      ...commonPurchaseStartedParams,
    })

    if (isCheckoutSkipped && selectedSubscription && window.axon) {
      window.axon(
        'track',
        'add_to_cart',
        getAxonCheckoutParams(selectedSubscription),
      )
    }
  }

export const purchaseAction =
  ({
    stripe,
    paymentPageId,
    card,
    createPaymentResFromDigitalWallet,
    upsellPages,
    sourceStep = SourceStep.CHECKOUT,
  }: {
    stripe: Stripe
    paymentPageId: string
    card?: StripeCardNumberElement
    createPaymentResFromDigitalWallet?: PaymentRequestPaymentMethodEvent
    upsellPages: string[]
    sourceStep?: SourceStep
  }): any =>
  async (
    dispatch: TAppDispatchThunk<any>,
    getState: () => IAppState,
  ): Promise<void> => {
    const state = getState()
    const planId = selectPlanId(state)
    const priceId = selectSubscriptionLookupKey(state)
    const trialPrice = selectSubscriptionTrialPeriodPrice(state)
    const uuid = selectUUID(state)
    const currentPrice = selectSubscriptionFullPrice(state)
    const trialPeriodDays = selectSubscriptionTrialPeriodDays(state)
    const currency = selectCurrency(state)
    const periodName = selectSubscriptionPeriodName(state)
    const periodQuantity = selectSubscriptionPeriodQuantity(state)
    const selectedSubscription = selectSubscription(state)
    const cohort = selectCurrentVariantCohort(state) as Cohort
    const email = selectEmail(state)

    if (!priceId || !currentPrice) {
      console.error('Error: no subscriptions plan selected')
      captureFunnelError(FunnelErrorCode.STRIPE_NO_PLAN_SELECTED, null, {
        tags: { payment_system: PaymentSystem.STRIPE },
        extra: { planId, priceId, currentPrice, paymentPageId },
      })
      return
    }

    dispatch(startFetching(PURCHASE))

    const paymentMethod =
      createPaymentResFromDigitalWallet?.paymentMethod?.card?.wallet?.type

    const isPaymentMethodFromDigitalWallet =
      paymentMethod === PaymentMethod.APPLE_PAY ||
      paymentMethod === PaymentMethod.GOOGLE_PAY

    const isCheckoutSkipped =
      isPaymentMethodFromDigitalWallet && sourceStep === SourceStep.PAYWALL

    const commonPurchaseStartedParams = getPurchaseStartedEventParams(state)
    const commonPurchaseSuccessParams = getPurchaseSuccessEventParams(state)
    const commonPurchaseFailedParams = getPurchaseFailedEventParams(state)

    eventLogger.logPurchaseStarted({
      paymentMethod,
      paymentSystem: PaymentSystem.STRIPE,
      sourceStep,
      eventName: isPaymentMethodFromDigitalWallet
        ? Events.PURCHASE_STARTED_POPUP
        : Events.PURCHASE_STARTED,
      ...commonPurchaseStartedParams,
    })

    logAddToCartPixelEvents({
      currency,
      email,
      periodQuantity,
      periodName,
      fullPrice: currentPrice,
      trialPrice,
      selectedSubscription: selectedSubscription as ISubscription,
      uuid,
    })

    if (isCheckoutSkipped && selectedSubscription && window.axon) {
      window.axon(
        'track',
        'begin_checkout',
        getAxonCheckoutParams(selectedSubscription),
      )
    }

    try {
      const createPaymentResponse =
        card && !createPaymentResFromDigitalWallet
          ? await stripe.createPaymentMethod({
              type: 'card',
              card,
              billing_details: { name: DEFAULT_CARDHOLDER_NAME },
            })
          : (createPaymentResFromDigitalWallet as PaymentMethodResult)

      if (
        !createPaymentResponse?.paymentMethod &&
        createPaymentResponse?.error
      ) {
        const {
          error: { type, code, message },
        } = createPaymentResponse

        eventLogger.logPurchaseFailed({
          error: {
            type,
            code,
            description: message,
          },
          paymentMethod,
          ...commonPurchaseFailedParams,
        })
        dispatch(setErrorAction(message || type))
        dispatch(stopFetching(PURCHASE))
        createPaymentResFromDigitalWallet?.complete('fail')
        return
      }

      const createSubscriptionResponse = await paymentApi.createSubscription({
        uuid,
        planId,
        trialPeriodDays,
        cohort,
        paymentMethodId: createPaymentResponse.paymentMethod.id,
      })

      if (
        !createSubscriptionResponse.success ||
        !createSubscriptionResponse.data
      ) {
        switch (createSubscriptionResponse.status) {
          case 402:
            dispatch(setErrorAction(i18n.t('purchase1.insufficientFunds')))
            break
          case 404:
            dispatch(setErrorAction(i18n.t('purchase1.linkIsNotValid')))
            break
          case 409:
            dispatch(setErrorAction(i18n.t('purchase1.haveSubscription')))
            break
          default:
            dispatch(setErrorAction(i18n.t('commonComponents.commonError')))
        }

        dispatch(stopFetching(PURCHASE))
        createPaymentResFromDigitalWallet?.complete('fail')
        return
      }

      dispatch(
        setPaymentClientSecretAction(
          createSubscriptionResponse.data.purchase.client_secret,
        ),
      )
      dispatch(
        setTrialPeriodDaysAction(
          createSubscriptionResponse.data.purchase.trial_period_days,
        ),
      )
      dispatch(
        setCreatedSubscriptionIdAction(
          createSubscriptionResponse.data.purchase.subscription_id,
        ),
      )

      const cardPaymentResponseFirst = await stripe.confirmCardPayment(
        createSubscriptionResponse.data.purchase.client_secret,
        {
          payment_method: createPaymentResponse.paymentMethod.id,
          save_payment_method: true,
          return_url: getRedirectUrl(paymentPageId),
        },
      )

      if (
        !cardPaymentResponseFirst?.paymentIntent &&
        cardPaymentResponseFirst?.error
      ) {
        logFailedPayment({
          paymentResponse: cardPaymentResponseFirst.error,
          paymentMethod,
          ...commonPurchaseFailedParams,
        })

        if (!checkIsRetryAllowed(cardPaymentResponseFirst)) {
          dispatch(
            setErrorAction(
              getErrorActionPayload(cardPaymentResponseFirst.error),
            ),
          )
          dispatch(stopFetching(PURCHASE))
          createPaymentResFromDigitalWallet?.complete('fail')
          return
        }

        const retryResponseFirst = await paymentApi.retryPayment({
          uuid,
          stripeError: cardPaymentResponseFirst.error as StripeError,
        })

        if (!retryResponseFirst.data.should_retry) {
          dispatch(
            setErrorAction(
              getErrorActionPayload(cardPaymentResponseFirst.error),
            ),
          )
          dispatch(stopFetching(PURCHASE))
          createPaymentResFromDigitalWallet?.complete('fail')
          return
        }

        dispatch(
          setPaymentClientSecretAction(
            retryResponseFirst.data.subscription.client_secret,
          ),
        )
        dispatch(
          setTrialPeriodDaysAction(
            retryResponseFirst.data.subscription.trial_period_days,
          ),
        )
        dispatch(
          setCreatedSubscriptionIdAction(
            retryResponseFirst.data.subscription.subscription_id,
          ),
        )

        const cardPaymentResponseSecond = await stripe.confirmCardPayment(
          retryResponseFirst.data.subscription.client_secret,
          {
            payment_method: createPaymentResponse.paymentMethod.id,
            save_payment_method: true,
            return_url: getRedirectUrl(paymentPageId),
          },
        )

        if (
          cardPaymentResponseSecond?.paymentIntent &&
          !cardPaymentResponseSecond?.error
        ) {
          const predictedLtvResponse = await userApi.getPredictedLtv(uuid)

          logSuccessfulPayment({
            ...commonPurchaseSuccessParams,
            predictedLtv: predictedLtvResponse?.data?.predicted_ltv,
            price:
              cardPaymentResponseSecond.paymentIntent.amount / CENTS_IN_DOLLAR,
            trialPrice:
              cardPaymentResponseSecond.paymentIntent.amount / CENTS_IN_DOLLAR,
            productId: createSubscriptionResponse.data.purchase.product_id,
            trialPeriodDays:
              retryResponseFirst.data.subscription.trial_period_days,
            subscriptionId:
              retryResponseFirst.data.subscription.subscription_id,
            discountApplied:
              retryResponseFirst.data.subscription.discount_applied,
            paymentMethod,
            paymentSystem: PaymentSystem.STRIPE,
          })
          dispatch(stopFetching(PURCHASE))
          dispatch(
            sendUserConfigAction(
              {
                payment_currency: currency,
                payment_method: paymentMethod || PaymentMethod.CREDIT_CARD,
                payment_system: PaymentSystem.STRIPE,
                is_user_signed_up: false,
                subscription_price: `${currentPrice}`,
                subscription_duration: `${periodQuantity}${periodName}`,
                price_id: priceId,
                trial_price: `${trialPrice}`,
                trial_period: `${trialPeriodDays}`,
                discount_applied:
                  !!retryResponseFirst.data.subscription.discount_applied,
                unvisited_upsell_pages: upsellPages,
              },
              null,
            ),
          )
          createPaymentResFromDigitalWallet?.complete('success')
          return
        }

        logFailedPayment({
          paymentResponse: cardPaymentResponseSecond.error,
          paymentMethod,
          ...commonPurchaseFailedParams,
        })

        if (!checkIsRetryAllowed(cardPaymentResponseSecond)) {
          dispatch(
            setErrorAction(
              getErrorActionPayload(cardPaymentResponseSecond.error),
            ),
          )
          dispatch(stopFetching(PURCHASE))
          createPaymentResFromDigitalWallet?.complete('fail')
          return
        }

        const retryResponseSecond = await paymentApi.retryPayment({
          uuid,
          stripeError: cardPaymentResponseSecond.error as StripeError,
        })

        if (!retryResponseSecond.data.should_retry) {
          dispatch(
            setErrorAction(
              getErrorActionPayload(cardPaymentResponseSecond.error),
            ),
          )
          dispatch(stopFetching(PURCHASE))
          createPaymentResFromDigitalWallet?.complete('fail')
          return
        }

        dispatch(
          setPaymentClientSecretAction(
            retryResponseSecond.data.subscription.client_secret,
          ),
        )
        dispatch(
          setTrialPeriodDaysAction(
            retryResponseSecond.data.subscription.trial_period_days,
          ),
        )
        dispatch(
          setCreatedSubscriptionIdAction(
            retryResponseSecond.data.subscription.subscription_id,
          ),
        )

        const cardPaymentResponseThird = await stripe.confirmCardPayment(
          retryResponseSecond.data.subscription.client_secret,
          {
            payment_method: createPaymentResponse.paymentMethod.id,
            save_payment_method: true,
            return_url: getRedirectUrl(paymentPageId),
          },
        )

        if (
          cardPaymentResponseThird?.paymentIntent &&
          !cardPaymentResponseThird?.error
        ) {
          const predictedLtvResponse = await userApi.getPredictedLtv(uuid)

          logSuccessfulPayment({
            ...commonPurchaseSuccessParams,
            predictedLtv: predictedLtvResponse?.data?.predicted_ltv,
            price:
              cardPaymentResponseThird.paymentIntent.amount / CENTS_IN_DOLLAR,
            trialPrice:
              cardPaymentResponseThird.paymentIntent.amount / CENTS_IN_DOLLAR,
            productId: createSubscriptionResponse.data.purchase.product_id,
            trialPeriodDays:
              retryResponseSecond.data.subscription.trial_period_days,
            subscriptionId:
              retryResponseSecond.data.subscription.subscription_id,
            discountApplied:
              retryResponseSecond.data.subscription.discount_applied,
            paymentMethod,
            paymentSystem: PaymentSystem.STRIPE,
          })
          dispatch(stopFetching(PURCHASE))
          dispatch(
            sendUserConfigAction(
              {
                payment_currency: currency,
                payment_method: paymentMethod || PaymentMethod.CREDIT_CARD,
                is_user_signed_up: false,
                subscription_price: `${currentPrice}`,
                subscription_duration: `${periodQuantity}${periodName}`,
                price_id: priceId,
                trial_price: `${trialPrice}`,
                trial_period: `${trialPeriodDays}`,
                payment_system: PaymentSystem.STRIPE,
                discount_applied:
                  !!retryResponseSecond.data.subscription.discount_applied,
                unvisited_upsell_pages: upsellPages,
              },
              null,
            ),
          )
          createPaymentResFromDigitalWallet?.complete('success')
          return
        }

        logFailedPayment({
          paymentResponse: cardPaymentResponseThird.error,
          paymentMethod,
          ...commonPurchaseFailedParams,
        })

        if (!checkIsRetryAllowed(cardPaymentResponseThird)) {
          dispatch(
            setErrorAction(
              getErrorActionPayload(cardPaymentResponseThird.error),
            ),
          )
          dispatch(stopFetching(PURCHASE))
          createPaymentResFromDigitalWallet?.complete('fail')
          return
        }

        const retryResponseThird = await paymentApi.retryPayment({
          uuid,
          stripeError: cardPaymentResponseThird.error as StripeError,
        })

        if (!retryResponseThird.data.should_retry) {
          dispatch(
            setErrorAction(
              getErrorActionPayload(cardPaymentResponseThird.error),
            ),
          )
          dispatch(stopFetching(PURCHASE))
          createPaymentResFromDigitalWallet?.complete('fail')
          return
        }

        dispatch(
          setPaymentClientSecretAction(
            retryResponseThird.data.subscription.client_secret,
          ),
        )
        dispatch(
          setTrialPeriodDaysAction(
            retryResponseThird.data.subscription.trial_period_days,
          ),
        )
        dispatch(
          setCreatedSubscriptionIdAction(
            retryResponseThird.data.subscription.subscription_id,
          ),
        )

        const cardPaymentResponseFourth = await stripe.confirmCardPayment(
          retryResponseThird.data.subscription.client_secret,
          {
            payment_method: createPaymentResponse.paymentMethod.id,
            save_payment_method: true,
            return_url: getRedirectUrl(paymentPageId),
          },
        )

        if (
          cardPaymentResponseFourth?.paymentIntent &&
          !cardPaymentResponseFourth?.error
        ) {
          const predictedLtvResponse = await userApi.getPredictedLtv(uuid)

          logSuccessfulPayment({
            ...commonPurchaseSuccessParams,
            predictedLtv: predictedLtvResponse?.data?.predicted_ltv,
            price:
              cardPaymentResponseFourth.paymentIntent.amount / CENTS_IN_DOLLAR,
            trialPrice:
              cardPaymentResponseFourth.paymentIntent.amount / CENTS_IN_DOLLAR,
            productId: createSubscriptionResponse.data.purchase.product_id,
            trialPeriodDays:
              retryResponseThird.data.subscription.trial_period_days,
            subscriptionId:
              retryResponseThird.data.subscription.subscription_id,
            discountApplied:
              retryResponseThird.data.subscription.discount_applied,
            paymentMethod,
            paymentSystem: PaymentSystem.STRIPE,
          })
          dispatch(stopFetching(PURCHASE))
          dispatch(
            sendUserConfigAction(
              {
                payment_currency: currency,
                payment_method: paymentMethod || PaymentMethod.CREDIT_CARD,
                is_user_signed_up: false,
                subscription_price: `${currentPrice}`,
                subscription_duration: `${periodQuantity}${periodName}`,
                price_id: priceId,
                trial_price: `${trialPrice}`,
                trial_period: `${trialPeriodDays}`,
                payment_system: PaymentSystem.STRIPE,
                discount_applied:
                  !!retryResponseThird.data.subscription.discount_applied,
                unvisited_upsell_pages: upsellPages,
              },
              null,
            ),
          )
          createPaymentResFromDigitalWallet?.complete('success')
          return
        }

        logFailedPayment({
          paymentResponse: cardPaymentResponseFourth.error,
          paymentMethod,
          ...commonPurchaseFailedParams,
        })

        dispatch(
          setErrorAction(
            getErrorActionPayload(cardPaymentResponseFourth.error),
          ),
        )

        // Needed for reset invoice on BE
        await paymentApi.retryPayment({
          uuid,
          stripeError: cardPaymentResponseFourth.error as StripeError,
        })

        dispatch(stopFetching(PURCHASE))
        createPaymentResFromDigitalWallet?.complete('fail')
        return
      }

      const { paymentIntent } = cardPaymentResponseFirst

      if (paymentIntent.status === 'requires_payment_method') {
        eventLogger.logPurchaseFailed({
          error: {
            type: 'requires_payment_method',
          },
          paymentMethod,
          ...commonPurchaseFailedParams,
        })
        dispatch(setErrorAction(i18n.t('purchase1.paymentFailed')))
        dispatch(stopFetching(PURCHASE))
        createPaymentResFromDigitalWallet?.complete('fail')
        return
      }

      const threeDSecureURL = paymentIntent.next_action?.redirect_to_url?.url

      if (paymentIntent.status === 'requires_action' && threeDSecureURL) {
        dispatch(set3DSecureIframeUrlAction(threeDSecureURL))
        dispatch(stopFetching(PURCHASE))
        return
      }

      const predictedLtvResponse = await userApi.getPredictedLtv(uuid)

      logSuccessfulPayment({
        price: currentPrice,
        predictedLtv: predictedLtvResponse?.data?.predicted_ltv,
        productId: createSubscriptionResponse.data.purchase.product_id,
        trialPeriodDays:
          createSubscriptionResponse.data.purchase.trial_period_days,
        subscriptionId:
          createSubscriptionResponse.data.purchase.subscription_id,
        paymentMethod,
        ...commonPurchaseSuccessParams,
        paymentSystem: PaymentSystem.STRIPE,
      })

      dispatch(stopFetching(PURCHASE))
      dispatch(
        sendUserConfigAction(
          {
            payment_currency: currency,
            payment_method: paymentMethod || PaymentMethod.CREDIT_CARD,
            is_user_signed_up: false,
            subscription_price: `${currentPrice}`,
            subscription_duration: `${periodQuantity}${periodName}`,
            price_id: priceId,
            trial_price: `${trialPrice}`,
            trial_period: `${trialPeriodDays}`,
            payment_system: PaymentSystem.STRIPE,
            unvisited_upsell_pages: upsellPages,
          },
          null,
        ),
      )
      createPaymentResFromDigitalWallet?.complete('success')
    } catch (error: any) {
      dispatch(setErrorAction(error.toString()))
      dispatch(stopFetching(PURCHASE))
      createPaymentResFromDigitalWallet?.complete('fail')
    }
  }

export const check3DSecure =
  (stripe: Stripe): any =>
  async (
    dispatch: TAppDispatchThunk<any>,
    getState: () => IAppState,
  ): Promise<void> => {
    const state = getState()
    const uuid = selectUUID(state)
    const priceId = selectSubscriptionLookupKey(state)
    const trialPriceId = selectSubscriptionLookupKey(state)
    const currentPrice = selectSubscriptionFullPrice(state)
    const clientSecret = selectPaymentClientSecret(state)
    const trialPeriodDays = selectTrialPeriodDays(state)
    const subscriptionId = selectCreatedSubscriptionId(state)
    const currency = selectCurrency(state)
    const periodName = selectSubscriptionPeriodName(state)
    const periodQuantity = selectSubscriptionPeriodQuantity(state)
    const trialPeriodQuantity = selectSubscriptionTrialPeriodDays(state)
    const trialPrice = selectSubscriptionTrialPeriodPrice(state)

    if (!priceId || !currentPrice) {
      console.error('Error: no subscriptions plan selected')
      captureFunnelError(FunnelErrorCode.STRIPE_3DS_NO_PLAN, null, {
        tags: { payment_system: PaymentSystem.STRIPE },
        extra: { priceId, currentPrice, subscriptionId },
      })
      return
    }

    if (!clientSecret) {
      console.error('Error: client secret is needed')
      captureFunnelError(FunnelErrorCode.STRIPE_3DS_NO_CLIENT_SECRET, null, {
        tags: { payment_system: PaymentSystem.STRIPE },
        extra: { priceId, subscriptionId },
      })
      return
    }

    dispatch(startFetching(CHECK_3D_SECURE))

    const response = await stripe.retrievePaymentIntent(clientSecret)
    const commonPurchaseSuccessParams = getPurchaseSuccessEventParams(state)
    const commonPurchaseFailedParams = getPurchaseFailedEventParams(state)

    if (response.paymentIntent?.status === 'succeeded') {
      const predictedLtvResponse = await userApi.getPredictedLtv(uuid)

      logSuccessfulPayment({
        price: currentPrice,
        trialPeriodDays,
        subscriptionId,
        productId: trialPriceId
          ? createIntroOfferProductId({
              priceId,
              trialPriceId,
              trialPeriodQuantity,
            })
          : priceId,
        ...commonPurchaseSuccessParams,
        paymentSystem: PaymentSystem.STRIPE,
        predictedLtv: predictedLtvResponse?.data?.predicted_ltv,
      })

      dispatch(reset3DSecureIframeUrlAction())
      dispatch(
        sendUserConfigAction(
          {
            payment_currency: currency,
            payment_method: PaymentMethod.CREDIT_CARD,
            is_user_signed_up: false,
            subscription_price: `${currentPrice}`,
            subscription_duration: `${periodQuantity}${periodName}`,
            price_id: priceId,
            trial_price: `${trialPrice}`,
            trial_period: `${trialPeriodDays}`,
          },
          null,
        ),
      )
      return
    }

    if (response.paymentIntent?.status === 'requires_payment_method') {
      eventLogger.logPurchaseFailed({
        error: {
          type: 'requires_payment_method',
        },
        ...commonPurchaseFailedParams,
      })
      dispatch(reset3DSecureIframeUrlAction())
      dispatch(setErrorAction(i18n.t('purchase1.paymentFailed')))
      dispatch(stopFetching(CHECK_3D_SECURE))
      return
    }

    if (response.error) {
      const {
        error: { type, code, message },
      } = response

      eventLogger.logPurchaseFailed({
        error: {
          type,
          code,
          description: message,
        },
        ...commonPurchaseFailedParams,
      })
      dispatch(reset3DSecureIframeUrlAction())
      dispatch(setErrorAction(message || type))
      dispatch(stopFetching(CHECK_3D_SECURE))
      return
    }

    dispatch(reset3DSecureIframeUrlAction())
    dispatch(setErrorAction(i18n.t('purchase1.3dSecureError')))
    dispatch(stopFetching(CHECK_3D_SECURE))
  }

export const clearUpiPurchaseDataAction =
  (): any =>
  async (
    dispatch: TAppDispatchThunk<any>,
    getState: () => IAppState,
  ): Promise<void> => {
    const uuid = selectUUID(getState())

    dispatch(updateUserConfigAction({ upiPurchaseData: null }))
    await userApi.saveUserConfig({
      uuid,
      config: { upi_purchase_data: null },
    })
  }

export const sendDeferredUpiPurchaseEventsAction =
  (): any =>
  (dispatch: TAppDispatchThunk<any>, getState: () => IAppState): void => {
    const state = getState()
    const upiPurchaseData = selectUpiPurchaseData(state)
    const userPaymentMethod = selectUserPaymentMethod(state)

    if (!upiPurchaseData) return

    if (userPaymentMethod === PaymentMethod.UPI) {
      const { uuid, email } = getPurchaseSuccessEventParams(state)

      logSuccessfulUpiPayment({ upiPurchaseData, uuid, email })
    }

    dispatch(clearUpiPurchaseDataAction())
  }

const failUpiPurchaseAction =
  ({
    error,
    purchaseFailedParams,
  }: {
    error: { type: string; code?: string; message?: string }
    purchaseFailedParams: ReturnType<typeof getPurchaseFailedEventParams>
  }): any =>
  (dispatch: TAppDispatchThunk<any>): void => {
    eventLogger.logPurchaseFailed({
      ...purchaseFailedParams,
      error: {
        type: error.type,
        code: error.code,
        description: error.message,
      },
      paymentMethod: PaymentMethod.UPI,
      paymentSystem: PaymentSystem.STRIPE,
    })
    dispatch(clearUpiPurchaseDataAction())
    dispatch(setErrorAction(i18n.t('purchase1.paymentFailed')))
    dispatch(stopFetching(PURCHASE))
  }

export const checkUpiRedirectPaymentAction =
  ({ stripe, clientSecret }: { stripe: Stripe; clientSecret: string }): any =>
  async (
    dispatch: TAppDispatchThunk<any>,
    getState: () => IAppState,
  ): Promise<void> => {
    const state = getState()
    const upiPurchaseData = selectUpiPurchaseData(state)

    if (!upiPurchaseData) return

    dispatch(startFetching(PURCHASE))

    let paymentIntent: PaymentIntent | undefined

    try {
      paymentIntent = (await stripe.retrievePaymentIntent(clientSecret))
        .paymentIntent
    } catch (error) {
      dispatch(stopFetching(PURCHASE))
      return
    }

    const isUpiPaymentAccepted =
      paymentIntent?.status === 'succeeded' ||
      paymentIntent?.status === 'processing'

    if (isUpiPaymentAccepted) {
      dispatch(setWasUpiPaymentInitiatedAction(true))
      await dispatch(getUserStatusAction(selectUUID(state)))
      dispatch(stopFetching(PURCHASE))
      return
    }

    const {
      priceId,
      trialPriceId,
      trialPeriodDays,
      periodName,
      periodQuantity,
      price,
      trialPrice,
      currency,
      screenName,
      stripeAccountId,
      stripeAccountName,
    } = upiPurchaseData

    dispatch(
      failUpiPurchaseAction({
        error: paymentIntent?.last_payment_error || { type: 'redirect_failed' },
        purchaseFailedParams: {
          productId: trialPriceId
            ? createIntroOfferProductId({
                priceId,
                trialPriceId,
                trialPeriodQuantity: trialPeriodDays,
              })
            : createProductId({ periodName, periodQuantity, price }),
          priceDetails: { price, trial: !!trialPrice, currency },
          screenName,
          stripeAccountId,
          stripeAccountName,
        },
      }),
    )
  }

export const upiPurchaseAction =
  ({
    stripe,
    upiId,
    billingName,
    billingAddress,
    postalCode,
    upsellPages,
  }: {
    stripe: Stripe
    upiId: string
    billingName: string
    billingAddress: string
    postalCode: string
    upsellPages: string[]
  }): any =>
  async (
    dispatch: TAppDispatchThunk<any>,
    getState: () => IAppState,
  ): Promise<void> => {
    const state = getState()
    const planId = selectPlanId(state)
    const priceId = selectSubscriptionLookupKey(state)
    const trialPrice = selectSubscriptionTrialPeriodPrice(state)
    const uuid = selectUUID(state)
    const currentPrice = selectSubscriptionFullPrice(state)
    const trialPeriodDays = selectSubscriptionTrialPeriodDays(state)
    const currency = selectCurrency(state)
    const periodName = selectSubscriptionPeriodName(state)
    const periodQuantity = selectSubscriptionPeriodQuantity(state)
    const selectedSubscription = selectSubscription(state)
    const cohort = selectCurrentVariantCohort(state) as Cohort
    const email = selectEmail(state)

    if (!priceId || !currentPrice) {
      captureFunnelError(FunnelErrorCode.STRIPE_NO_PLAN_SELECTED, null, {
        tags: { payment_system: PaymentSystem.STRIPE },
        extra: { planId, priceId, currentPrice },
      })
      return
    }

    dispatch(startFetching(PURCHASE))
    dispatch(setWasUpiPaymentInitiatedAction(false))

    const commonPurchaseStartedParams = getPurchaseStartedEventParams(state)
    const commonPurchaseSuccessParams = getPurchaseSuccessEventParams(state)
    const commonPurchaseFailedParams = getPurchaseFailedEventParams(state)

    eventLogger.logPurchaseStarted({
      paymentMethod: PaymentMethod.UPI,
      paymentSystem: PaymentSystem.STRIPE,
      ...commonPurchaseStartedParams,
    })

    logAddToCartPixelEvents({
      currency,
      email,
      periodQuantity,
      periodName,
      fullPrice: currentPrice,
      trialPrice,
      selectedSubscription: selectedSubscription as ISubscription,
      uuid,
    })

    try {
      const createSubscriptionResponse = await paymentApi.createSubscription({
        uuid,
        planId,
        trialPeriodDays,
        cohort,
        upiVpa: upiId,
      })

      if (
        !createSubscriptionResponse.success ||
        !createSubscriptionResponse.data
      ) {
        switch (createSubscriptionResponse.status) {
          case 402:
            dispatch(setErrorAction(i18n.t('purchase1.insufficientFunds')))
            break
          case 404:
            dispatch(setErrorAction(i18n.t('purchase1.linkIsNotValid')))
            break
          case 409:
            dispatch(setErrorAction(i18n.t('purchase1.haveSubscription')))
            break
          default:
            dispatch(setErrorAction(i18n.t('commonComponents.commonError')))
        }

        dispatch(stopFetching(PURCHASE))
        return
      }

      const { purchase } = createSubscriptionResponse.data
      const predictedLtvResponse = await userApi.getPredictedLtv(uuid)

      const upiPurchaseData: IUpiPurchaseData = {
        productId: purchase.product_id,
        subscriptionId: purchase.subscription_id,
        paymentIntentId: purchase.client_secret.split('_secret_')[0],
        priceId,
        trialPriceId: selectSubscriptionTrialLookupKey(state),
        price: currentPrice,
        trialPrice,
        trialPeriodDays: purchase.trial_period_days,
        periodName,
        periodQuantity,
        currency,
        screenName: commonPurchaseSuccessParams.screenName,
        stripeAccountId: commonPurchaseSuccessParams.stripeAccountId,
        stripeAccountName: commonPurchaseSuccessParams.stripeAccountName,
        predictedLtv: predictedLtvResponse?.data?.predicted_ltv,
      }

      let isUpiPurchaseDataSaved = true

      await dispatch(
        sendUserConfigAction(
          {
            payment_currency: currency,
            payment_method: PaymentMethod.UPI,
            payment_system: PaymentSystem.STRIPE,
            is_user_signed_up: false,
            subscription_price: `${currentPrice}`,
            subscription_duration: `${periodQuantity}${periodName}`,
            price_id: priceId,
            trial_price: `${trialPrice}`,
            trial_period: `${trialPeriodDays}`,
            unvisited_upsell_pages: upsellPages,
            upi_purchase_data: upiPurchaseData,
          },
          () => {
            isUpiPurchaseDataSaved = false
          },
        ),
      )

      if (!isUpiPurchaseDataSaved) {
        dispatch(stopFetching(PURCHASE))
        return
      }

      dispatch(updateUserConfigAction({ upiPurchaseData }))

      const upiPaymentResponse = await (stripe as any).confirmUpiPayment(
        purchase.client_secret,
        {
          payment_method: {
            upi: {},
            billing_details: {
              name: billingName,
              email,
              address: {
                country: UPI_BILLING_COUNTRY,
                line1: billingAddress,
                postal_code: postalCode,
              },
            },
          },
          return_url: window.location.href,
        },
      )

      if (upiPaymentResponse.error) {
        dispatch(
          failUpiPurchaseAction({
            error: upiPaymentResponse.error,
            purchaseFailedParams: commonPurchaseFailedParams,
          }),
        )
        return
      }

      const { paymentIntent: confirmedPaymentIntent } = upiPaymentResponse
      const isUpiPaymentUnfinished =
        confirmedPaymentIntent.status === 'requires_action' ||
        confirmedPaymentIntent.status === 'requires_payment_method'
      const paymentIntent = isUpiPaymentUnfinished
        ? (await stripe.retrievePaymentIntent(purchase.client_secret))
            .paymentIntent || confirmedPaymentIntent
        : confirmedPaymentIntent

      if (paymentIntent.last_payment_error) {
        dispatch(
          failUpiPurchaseAction({
            error: paymentIntent.last_payment_error,
            purchaseFailedParams: commonPurchaseFailedParams,
          }),
        )
        return
      }

      if (paymentIntent.status === 'succeeded') {
        logSuccessfulUpiPayment({
          upiPurchaseData,
          uuid,
          email: commonPurchaseSuccessParams.email,
        })
        await dispatch(clearUpiPurchaseDataAction())
        dispatch(stopFetching(PURCHASE))
        return
      }

      if (paymentIntent.status === 'processing') {
        dispatch(setWasUpiPaymentInitiatedAction(true))
        dispatch(stopFetching(PURCHASE))
        return
      }

      dispatch(
        failUpiPurchaseAction({
          error: { type: paymentIntent.status },
          purchaseFailedParams: commonPurchaseFailedParams,
        }),
      )
    } catch (error: any) {
      dispatch(setErrorAction(error.toString()))
      dispatch(stopFetching(PURCHASE))
    }
  }

export const makeInappUpsellAction =
  (restUnvisitedUpsellPages: string[]): any =>
  async (
    dispatch: TAppDispatchThunk<any>,
    getState: () => IAppState,
  ): Promise<void> => {
    const state = getState()
    const uuid = selectUUID(state)
    const planId = selectPlanId(state)
    const paymentMethod = selectUserPaymentMethod(state)
    const productId = selectProductId(state)
    const selectedSubscription = selectSubscription(state)
    const paymentSystem = selectUserPaymentSystem(state)
    const screenName = selectScreenName(state)
    const currency = selectCurrency(state)
    const stripeAccountName = selectStripeAccountName(state)
    const stripeAccountId = selectStripeAccountId(state)
    const price = selectSubscriptionTrialPeriodPrice(state)
    const email = selectEmail(state)
    const periodQuantity = selectSubscriptionPeriodQuantity(state)
    const periodName = selectSubscriptionPeriodName(state)
    const fullPrice = selectSubscriptionTrialPeriodPrice(state)

    dispatch(startFetching(MAKE_INAPP_UPSELL))

    try {
      eventLogger.logInAppPurchaseStarted({
        price,
        currency,
        paymentMethod,
        paymentSystem,
        screenName,
        productId,
        stripeAccountName,
        stripeAccountId,
      })

      logAddToCartPixelEvents({
        currency,
        email,
        periodQuantity,
        periodName,
        fullPrice,
        selectedSubscription: selectedSubscription as ISubscription,
        uuid,
        productName: productId,
        isUpsell: true,
        isOneTimeUpsell: true,
      })

      const inappUpsellResponse = await paymentApi.makeInappPurchase({
        uuid,
        planId: Number(planId),
        productName: productId,
      })

      if (inappUpsellResponse.success && inappUpsellResponse.data) {
        eventLogger.logInAppPurchaseCompleted({
          price,
          currency,
          paymentMethod,
          paymentSystem,
          screenName,
          productId,
          stripeAccountName,
          stripeAccountId,
        })

        await dispatch(
          sendUserConfigAction({
            unvisited_upsell_pages: restUnvisitedUpsellPages,
          }),
        )

        dispatch(stopFetching(MAKE_INAPP_UPSELL))
        return
      }

      let errorText: string

      switch (inappUpsellResponse.data?.decline_code) {
        case StripeSoftDeclineReason.INSUFFICIENT_FUNDS:
          errorText = i18n.t('purchase1.insufficientFunds')
          dispatch(setErrorAction(errorText))
          break
        case StripeSoftDeclineReason.CARD_VELOCITY_EXCEEDED:
          errorText = i18n.t('purchase1.velocityExceeded')
          dispatch(setErrorAction(errorText))
          break
        case StripeDeclineReason.STOLEN_CARD:
        case StripeDeclineReason.LOST_CARD:
        case StripeDeclineReason.STRIPE_GENERIC_DECLINE_ERROR:
          errorText = i18n.t('purchase1.genericDecline')
          dispatch(setErrorAction(errorText))
          break
        default:
          errorText = i18n.t('commonComponents.commonError')
          dispatch(setErrorAction(errorText))
      }

      eventLogger.logInAppPurchaseFailed({
        error: {
          type: inappUpsellResponse.data?.error,
          code: inappUpsellResponse.data?.decline_code,
          description: errorText,
        },
        price,
        currency,
        paymentMethod,
        paymentSystem,
        screenName,
        productId,
        stripeAccountName,
        stripeAccountId,
      })

      dispatch(stopFetching(MAKE_INAPP_UPSELL))
    } catch (error: unknown) {
      dispatch(stopFetching(MAKE_INAPP_UPSELL))
    }
  }

export const makeUpsellAction =
  (restUnvisitedUpsellPages: string[]): any =>
  async (
    dispatch: TAppDispatchThunk<any>,
    getState: () => IAppState,
  ): Promise<void> => {
    const state = getState()
    const uuid = selectUUID(state)
    const planId = selectPlanId(state)
    const trialPeriodDays = selectTrialPeriodDays(state)
    const paymentMethod = selectUserPaymentMethod(state)
    const productId = selectProductId(state)
    const paymentSystem = selectUserPaymentSystem(state)
    const screenName = selectScreenName(state)
    const currentPrice = selectSubscriptionFullPrice(state)
    const periodName = selectSubscriptionPeriodName(state)
    const periodQuantity = selectSubscriptionPeriodQuantity(state)
    const selectedSubscription = selectSubscription(state)
    const currency = selectCurrency(state)
    const email = selectEmail(state)

    dispatch(startFetching(MAKE_UPSELL))

    logAddToCartPixelEvents({
      currency,
      email,
      periodQuantity,
      periodName,
      fullPrice: currentPrice,
      selectedSubscription: selectedSubscription as ISubscription,
      uuid,
      isUpsell: true,
    })

    try {
      const commonPurchaseStartedParams = getPurchaseStartedEventParams(state)
      const commonPurchaseSuccessParams = getPurchaseSuccessEventParams(state)
      const commonPurchaseFailedParams = getPurchaseFailedEventParams(state)

      eventLogger.logPurchaseStarted({
        ...commonPurchaseStartedParams,
        paymentMethod,
        paymentSystem,
        productKey: productId,
      })

      const upsellResponse =
        paymentSystem === PaymentSystem.PRIMER
          ? await paymentApi.makePrimerUpsell({
              uuid,
              productId,
              planId,
            })
          : await paymentApi.makeUpsell({ uuid, productId, planId })

      if (upsellResponse.success && upsellResponse.data) {
        logSuccessfulPayment({
          ...commonPurchaseSuccessParams,
          price: currentPrice,
          trialPeriodDays,
          subscriptionId: upsellResponse.data.subscription.subscription_id,
          paymentMethod,
          paymentSystem,
          productId: upsellResponse.data.subscription.product_id,
          productKey: productId,
          screenName,
          isUpsell: true,
        })

        await dispatch(
          sendUserConfigAction({
            unvisited_upsell_pages: restUnvisitedUpsellPages,
          }),
        )

        dispatch(stopFetching(MAKE_UPSELL))
        return
      }

      let errorText: string

      switch (upsellResponse.data?.decline_code) {
        case StripeSoftDeclineReason.INSUFFICIENT_FUNDS:
          errorText = i18n.t('purchase1.insufficientFunds')
          dispatch(setErrorAction(errorText))
          break
        case StripeSoftDeclineReason.CARD_VELOCITY_EXCEEDED:
          errorText = i18n.t('purchase1.velocityExceeded')
          dispatch(setErrorAction(errorText))
          break
        case StripeDeclineReason.STOLEN_CARD:
        case StripeDeclineReason.LOST_CARD:
        case StripeDeclineReason.STRIPE_GENERIC_DECLINE_ERROR:
          errorText = i18n.t('purchase1.genericDecline')
          dispatch(setErrorAction(errorText))
          break
        default:
          errorText = i18n.t('commonComponents.commonError')
          dispatch(setErrorAction(errorText))
      }

      eventLogger.logPurchaseFailed({
        ...commonPurchaseFailedParams,
        paymentMethod,
        paymentSystem,
        productKey: productId,
        error: {
          type: upsellResponse.data?.error,
          code: upsellResponse.data?.decline_code,
          description: errorText,
        },
      })

      dispatch(stopFetching(MAKE_UPSELL))
    } catch (error: unknown) {
      dispatch(stopFetching(MAKE_UPSELL))
    }
  }
